import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import Markdown from "../components/Markdown";
import { Sparkles, Send, History, ArrowRight } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/39141d99-ef4f-4686-a207-2702e1fdeb56/images/976fe523e979a7a74446917beadc12695acf024ad503eb9deebae737e3d9e41e.png";
const PARCHMENT = "https://static.prod-images.emergentagent.com/jobs/39141d99-ef4f-4686-a207-2702e1fdeb56/images/71961a88075b238f2c3ac01d6af1c6efc499931b1c198a38074b5530f81c88c1.png";

export default function Guidance() {
  const [problem, setProblem] = useState("");
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/me/guidance");
      setHistory(data);
    } catch {}
  };
  useEffect(() => { loadHistory(); }, []);

  const ask = async (e) => {
    e.preventDefault();
    if (problem.trim().length < 10) {
      setError("Share a few sentences (at least 10 characters) so Krishna can guide you.");
      return;
    }
    setError("");
    setLoading(true);
    setAnswer(null);
    try {
      const { data } = await api.post("/guidance", { problem: problem.trim() });
      setAnswer(data);
      loadHistory();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="guidance-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 pt-16 lg:pt-20 pb-10">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7 animate-fade-up">
              <span className="tag inline-flex items-center gap-2">
                <Sparkles size={14} /> A conversation with Krishna
              </span>
              <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-[#2D3B31] mt-5 leading-tight">
                Pour out your heart.
                <br />
                <span className="text-[#D96C4A] italic">Receive a verse.</span>
              </h1>
              <p className="mt-5 text-[#5C6B5F] leading-relaxed max-w-xl">
                Describe what is troubling you — anxiety, anger, loss, doubt, a hard
                decision — in your own words. We will find the verse from the Gītā
                that speaks most directly to your moment, and offer guidance grounded
                in it.
              </p>
            </div>
            <div className="md:col-span-5">
              <div className="relative animate-fade-up animate-delay-2">
                <div className="absolute -inset-6 rounded-[2rem] bg-[#F2D8CF] opacity-40 blur-2xl" />
                <div className="relative rounded-[2rem] overflow-hidden border border-[#EAE5D9] aspect-square">
                  <img src={HERO_IMG} alt="Cosmic mandala" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="max-w-3xl mx-auto px-6 lg:px-12 pb-10">
        <form onSubmit={ask} className="organic-card p-8 md:p-10" data-testid="guidance-form">
          <label className="text-xs uppercase tracking-widest text-[#8A948C]">
            What is on your mind?
          </label>
          <textarea
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
            className="input-organic mt-3 min-h-[160px] font-sans-ui"
            placeholder="I am struggling with… / I feel… / I need help with…"
            minLength={10}
            maxLength={4000}
            required
            data-testid="guidance-problem-input"
          />
          <div className="flex items-center justify-between mt-2">
            <div className="text-xs text-[#8A948C]">{problem.length}/4000</div>
            <button
              type="submit"
              disabled={loading}
              className="btn-accent inline-flex items-center gap-2"
              data-testid="guidance-submit-btn"
            >
              <Send size={16} />
              {loading ? "Listening…" : "Ask Krishna"}
            </button>
          </div>
          {error && <div className="mt-4 text-sm text-[#C05B3B]" data-testid="guidance-error">{error}</div>}
        </form>
      </section>

      {/* Answer */}
      {(loading || answer) && (
        <section className="max-w-3xl mx-auto px-6 lg:px-12">
          {loading && (
            <div className="organic-card p-10 text-center" data-testid="guidance-loading">
              <div className="w-10 h-10 mx-auto rounded-full border-2 border-[#EAE5D9] border-t-[#D96C4A] animate-spin" />
              <p className="mt-4 text-sm text-[#8A948C] italic font-serif-display">
                Drawing from the Gītā…
              </p>
            </div>
          )}
          {answer && (
            <div className="space-y-6 animate-fade-up" data-testid="guidance-answer">
              <div
                className="relative rounded-[2rem] overflow-hidden border p-10 md:p-14"
                style={{ borderColor: "#EAE5D9", backgroundImage: `url(${PARCHMENT})`, backgroundSize: "cover" }}
              >
                <div className="absolute inset-0 bg-white/85" />
                <div className="relative">
                  <div className="text-xs tracking-widest uppercase text-[#D96C4A]">
                    {answer.intent && answer.intent !== "other" ? `On ${answer.intent}` : "A reflection"}
                  </div>
                  <h2 className="font-serif-display text-3xl sm:text-4xl text-[#2D3B31] mt-2" data-testid="guidance-title">
                    {answer.title}
                  </h2>

                  <div className="mt-8 space-y-6">
                    {(answer.matched_verses || []).map((v) => (
                      <div key={v.reference} className="border-l-4 pl-5 py-2" style={{ borderColor: "#D96C4A" }} data-testid={`guidance-verse-${v.reference}`}>
                        <div className="text-xs tracking-widest uppercase text-[#D96C4A]">Gītā {v.reference}</div>
                        {v.sanskrit && (
                          <p className="shloka mt-2 text-xl text-[#2D3B31]">{v.sanskrit}</p>
                        )}
                        <p className="mt-2 text-base italic text-[#2D3B31] font-serif-display">
                          “{v.translation}”
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-10 text-base leading-relaxed text-[#2D3B31] whitespace-pre-wrap" data-testid="guidance-message">
                    {answer.guidance}
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <Link to="/chapters" className="btn-outline inline-flex items-center gap-2" data-testid="guidance-explore-chapters">
                  Explore the chapters <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          )}
        </section>
      )}

      {/* History */}
      {history.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 lg:px-12 mt-16 pb-20">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-xs uppercase tracking-widest text-[#8A948C] hover:text-[#2D3B31] inline-flex items-center gap-2"
            data-testid="guidance-toggle-history"
          >
            <History size={14} /> Past reflections ({history.length}) {showHistory ? "▴" : "▾"}
          </button>
          {showHistory && (
            <div className="mt-6 space-y-3" data-testid="guidance-history-list">
              {history.map((h) => (
                <div key={h.id} className="organic-card p-5">
                  <div className="text-xs text-[#8A948C]">
                    {new Date(h.created_at).toLocaleString()} · {h.intent}
                  </div>
                  <div className="font-serif-display text-lg text-[#2D3B31] mt-1">{h.title}</div>
                  <div className="text-sm text-[#5C6B5F] mt-2 line-clamp-2 italic">"{h.problem}"</div>
                  <button
                    onClick={() => { setAnswer(h); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className="text-xs text-[#D96C4A] mt-2"
                    data-testid={`guidance-history-open-${h.id}`}
                  >
                    Read reflection →
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
